<template>
	<div
		class="form-input"
		:class="{ 'form-input--error': errorMessage }">
		<div class="form-input__prepend-label-container">
			<slot name="prepend-label" />
		</div>
		<div
			class="form-input__label-container"
			:class="{
				'form-input__label-container--inline-input':
					$slots['prepend-input'] || $slots['append-input'],
			}">
			<div
				v-if="$slots['prepend-input']"
				ref="prependInputContainer"
				class="form-input__input-container">
				<slot name="prepend-input" />
			</div>
			<slot
				name="label"
				:input-id="inputId">
				<label
					:id="labelId"
					:for="inputId">
					{{ labelText }}
				</label>
			</slot>
			<div
				v-if="$slots['append-input']"
				ref="appendInputContainer"
				class="form-input__input-container">
				<slot name="append-input" />
			</div>
		</div>
		<div
			v-if="$slots.default"
			ref="defaultInputContainer"
			class="form-input__input-container">
			<slot :input-id="inputId" />
		</div>
		<div class="form-input__error-message-container">
			<slot
				name="error-message"
				:error-id="errorId">
				<small
					v-if="errorMessage"
					:id="errorId"
					class="form-input__error-message"
					>{{ errorMessage }}</small
				>
			</slot>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, useId, watchEffect } from 'vue'

const props = defineProps<{
	errorMessage?: string
	required?: boolean
	label?: string
}>()

const prependInputContainer = ref<HTMLElement | null>(null)
const appendInputContainer = ref<HTMLElement | null>(null)
const defaultInputContainer = ref<HTMLElement | null>(null)
const inputId = useId()
const labelId = useId()
const errorId = useId()

const labelText = computed(() => {
	return props.required ? `${props.label} *` : props.label
})

const updateAriaAttributes = (element: HTMLElement, isNativeInput: boolean) => {
	if (props.required) {
		element.setAttribute('aria-required', 'true')
	} else {
		element.removeAttribute('aria-required')
	}

	if (props.errorMessage) {
		element.setAttribute('aria-describedby', errorId)
		element.setAttribute('aria-invalid', 'true')
	} else {
		element.removeAttribute('aria-describedby')
		element.removeAttribute('aria-invalid')
	}

	// A label's for attribute names native inputs only; other controls point at the label, unless
	// they carry their own name, such as the customer chooser's "Izberi stranko" button.
	if (!isNativeInput && !element.hasAttribute('aria-label')) {
		element.setAttribute('aria-labelledby', labelId)
	}
}

const processInputContainer = (container: HTMLElement | null) => {
	if (!container || Array.isArray(container)) return

	// Try to find a native input element first
	const inputElement = container.querySelector('input, textarea, select')

	if (inputElement) {
		// For native input elements, set the id
		inputElement.id = inputId

		// Watch for prop changes and update aria attributes
		watchEffect(() => {
			updateAriaAttributes(inputElement as HTMLElement, true)
		})
	} else {
		// For non-native elements (like PrimeVue components without native inputs), name the
		// element that takes the focus, such as Select's combobox, else the component's root.
		const componentElement =
			container.querySelector('[role="combobox"], [tabindex="0"]') ??
			container.querySelector('[class*="p-"]')

		if (componentElement) {
			// Watch for prop changes and update aria attributes
			watchEffect(() => {
				updateAriaAttributes(componentElement as HTMLElement, false)
			})
		}
	}
}

onMounted(() => {
	// Process all input containers
	processInputContainer(prependInputContainer.value)
	processInputContainer(appendInputContainer.value)
	processInputContainer(defaultInputContainer.value)
})
</script>

<style scoped lang="scss">
.form-input {
	display: flex;
	flex-direction: column;
	gap: 5px;

	&--error {
		:deep(.form-input__input-container) {
			.p-inputtext,
			.p-cascadeselect,
			.p-checkbox-box,
			.p-colorpicker,
			.p-datepicker,
			.p-editor,
			.p-inputnumber-input,
			.p-inputotp-input,
			.p-listbox,
			.p-multiselect,
			.p-radiobutton-input,
			.p-select,
			.p-selectbutton,
			.p-textarea,
			.p-togglebutton,
			.p-toggleswitch .p-toggleswitch-slider,
			.p-treeselect {
				border: 1px solid var(--p-red-500);
			}

			.p-slider .p-slider-handle,
			.p-slider .p-slider-handle::before {
				background-color: var(--p-red-500);
			}

			.p-knob svg .p-knob-range {
				stroke: var(--p-red-500);
			}
			.p-knob svg .p-knob-text,
			.p-rating svg path {
				fill: var(--p-red-500);
			}
		}
	}

	.form-input__label-container {
		display: flex;
		justify-content: flex-start;
		gap: 5px;
		flex-direction: row;

		&--inline-input {
			gap: 10px;
		}
	}

	.form-input__input-container {
		> :not(.p-toggleswitch) {
			width: 100%;
		}

		:deep(.p-datepicker-input) {
			width: 100%;
		}
	}

	.form-input__error-message-container {
		display: flex;
		align-items: flex-start;
		gap: 5px;
		flex-direction: column;

		// The theme's invalid-field text: red-600 on light, red-400 on dark surfaces.
		.form-input__error-message {
			color: var(--p-form-field-invalid-placeholder-color);
		}
	}
}
</style>
